import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { buildAttachmentLightboxSlide } from '../../lib/lightboxSlides';
import { cn } from '../../lib/utils';

import Attachment from './Attachment';
import UnreadMarker from './UnreadMarker';

// Chat rows provide these variables; modal/storybook contexts fall back to 0.
// Left includes the centered-column gutter plus the fixed offset from row column
// to message content. Right only needs the gutter plus any row rail breathing room —
// Message.jsx hands its hover cluster in as a trailing scroller item precisely so nothing
// sits beside the scroller and eats into the reach.
const SCROLLER_FRAME_STYLE = {
  '--scroller-bleed-left':
    'calc(var(--message-column-gutter, 0px) + var(--message-content-start-offset, 0px) + var(--message-rail-bleed-left, 0px))',
  '--scroller-bleed-right': 'calc(var(--message-column-gutter, 0px) + var(--message-rail-bleed-right, 0px))',
};

// Drag-to-pan the attachment row on desktop without breaking taps on individual tiles.
// Touch already pans natively — we only intercept mouse. Tiny moves are treated as clicks;
// past 6px we commit to a pan and swallow the trailing click so we don't open a lightbox.
const ATTACHMENT_PAN_THRESHOLD_PX = 6;
const ATTACHMENT_PAN_SUPPRESS_CLICK_MS = 250;

// Soften the hard cut where the row runs out of panel. This is an alpha mask, not a
// colored overlay: the tiles themselves fade to transparent, so whatever is behind them
// (white, dark, or a workspace background image) shows through untouched.
// The ramp starts this far in and is fully clear well before the edge, so the last stretch
// is pure background rather than a thin sliver of image.
const SCROLLER_EDGE_FADE_PX = 180;
const SCROLLER_EDGE_CLEAR_PX = 64;
// A straight linear alpha ramp leaves visible knees where it meets full opacity and full
// clear — the eye reads those corners as a hard edge. Sampling a smoothstep instead rounds
// both ends, so the tile eases out of view rather than being cut into.
const SCROLLER_EDGE_FADE_STOPS = 12;
const SCROLLER_EDGE_FADE = `linear-gradient(to right, ${Array.from({ length: SCROLLER_EDGE_FADE_STOPS }, (_, stopIndex) => {
  const progress = stopIndex / (SCROLLER_EDGE_FADE_STOPS - 1);
  const alpha = 1 - progress * progress * (3 - 2 * progress);
  const inset = SCROLLER_EDGE_FADE_PX - progress * (SCROLLER_EDGE_FADE_PX - SCROLLER_EDGE_CLEAR_PX);
  return `rgba(0, 0, 0, ${alpha.toFixed(3)}) calc(100% - ${inset.toFixed(2)}px)`;
}).join(', ')})`;
const SCROLLER_EDGE_FADE_STYLE = { maskImage: SCROLLER_EDGE_FADE, WebkitMaskImage: SCROLLER_EDGE_FADE };
// Sub-pixel scroll positions never land exactly on the end; treat anything under a pixel
// as fully scrolled so the mask lifts instead of clinging to the last tile.
const SCROLLER_END_EPSILON_PX = 1;

// Thumbhash and the sharp local thumbnail are generated together, so this preserves
// image preview state across reconciliation. Other attachments have no preview to retain.
const attachmentRenderKey = (attachment, index) =>
  `${index}:${attachment?.metadata?.thumbhash || attachment?.attachment_id || attachment?.id}`;

function useAttachmentScrollerPanning({ attachmentsCount }) {
  const scrollerRef = useRef(null);
  const dragStateRef = useRef(null);
  const suppressClickUntilRef = useRef(0);
  const [isPanning, setIsPanning] = useState(false);
  // Tracks whether the row actually overflows — drives the grab cursor so we don't
  // promise a drag affordance when there's nothing to pan.
  const [canPan, setCanPan] = useState(false);
  // Only fade while content is still cut off. At the end of the row the last tile is
  // whole, and fading it there would read as a rendering bug rather than an affordance.
  const [isCutOffRight, setIsCutOffRight] = useState(false);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const measure = () => {
      const overflow = scroller.scrollWidth - scroller.clientWidth;
      setCanPan(overflow > 0);
      setIsCutOffRight(overflow - scroller.scrollLeft > SCROLLER_END_EPSILON_PX);
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    scroller.addEventListener('scroll', measure, { passive: true });
    return () => {
      observer.disconnect();
      scroller.removeEventListener('scroll', measure);
    };
  }, [attachmentsCount]);

  const handlePointerDown = useCallback((event) => {
    // Mouse + left button only; trackpad/touch keep their native scroll behavior.
    if (!event.isPrimary || event.pointerType !== 'mouse' || event.button !== 0) return;

    suppressClickUntilRef.current = 0;

    // Nothing to pan if the row already fits.
    const scroller = event.currentTarget;
    if (scroller.scrollWidth <= scroller.clientWidth) return;

    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: scroller.scrollLeft,
      isPanning: false,
    };
  }, []);

  const handlePointerMove = useCallback((event) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - dragState.startX;
    if (!dragState.isPanning) {
      // Below the threshold we're still potentially a click — don't hijack the gesture yet.
      if (Math.abs(deltaX) < ATTACHMENT_PAN_THRESHOLD_PX) return;

      // Committed to a pan. Capture so a fast drag off the row keeps tracking.
      dragState.isPanning = true;
      setIsPanning(true);
      event.currentTarget.setPointerCapture(event.pointerId);
    }

    event.preventDefault();
    event.currentTarget.scrollLeft = dragState.startScrollLeft - deltaX;
  }, []);

  const handlePointerEnd = useCallback((event) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    if (dragState.isPanning) {
      // The browser will fire a click after pointerup — eat the next one so we don't
      // open the tile the user happened to release over.
      suppressClickUntilRef.current = event.timeStamp + ATTACHMENT_PAN_SUPPRESS_CLICK_MS;

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    }

    dragStateRef.current = null;
    setIsPanning(false);
  }, []);

  const handleClickCapture = useCallback((event) => {
    // Capture phase runs before any child onClick, so we can kill the click before
    // a tile's lightbox handler ever sees it.
    if (!suppressClickUntilRef.current || event.timeStamp > suppressClickUntilRef.current) return;

    suppressClickUntilRef.current = 0;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDragStart = useCallback((event) => {
    // Stops the browser's native image-drag ghost from firing during a pan.
    if (!dragStateRef.current) return;

    event.preventDefault();
  }, []);

  return {
    isPanning,
    canPan,
    isCutOffRight,
    scrollerRef,
    scrollerPanningProps: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerEnd,
      onPointerCancel: handlePointerEnd,
      onLostPointerCapture: handlePointerEnd,
      onClickCapture: handleClickCapture,
      onDragStart: handleDragStart,
    },
  };
}

export const MessageAttachments = ({
  attachments,
  shouldShowUnreadMarker = false,
  channelId,
  messageId,
  messageSortOrder,
  onUnreadMarkerDismiss,
  unreadMarkerWorkspaceId,
  unreadMarkerProfileId,
  onOpenImageLightbox,
  // Rides inside the scroller after the final tile so it never covers an image.
  trailingAccessory = null,
}) => {
  const attachmentsCount = attachments?.length ?? 0;
  const { isPanning, canPan, isCutOffRight, scrollerRef, scrollerPanningProps } = useAttachmentScrollerPanning({ attachmentsCount });
  const hasAttachments = attachmentsCount > 0;
  // Build a message-level gallery once so each tile knows "all siblings + my index".
  const imageGallery = useMemo(() => {
    const slides = [];
    const indexByAttachmentId = new Map();

    (attachments || []).forEach((attachment) => {
      const slide = buildAttachmentLightboxSlide(attachment);
      if (!slide) return;

      const slideIndex = slides.push(slide) - 1;
      if (attachment?.id) {
        indexByAttachmentId.set(attachment.id, slideIndex);
      }
    });

    return { slides, indexByAttachmentId };
  }, [attachments]);
  // Single lightboxable attachments stay in plain lightbox mode; gallery mode is only 2+ slides.
  const imageGallerySlides = imageGallery.slides.length > 1 ? imageGallery.slides : null;

  if (!hasAttachments) {
    return null;
  }

  if (attachmentsCount > 1) {
    return (
      <div className="relative w-full min-w-0" style={SCROLLER_FRAME_STYLE}>
        <div className="relative ml-[calc(var(--scroller-bleed-left)*-1)] w-[calc(100%+var(--scroller-bleed-left)+var(--scroller-bleed-right))] min-w-0">
          {/* The old floating reaction chip lived here; image messages now use the
              standard hover cluster rendered by Message.jsx beside the bubble. */}
          {shouldShowUnreadMarker && (
            <UnreadMarker
              workspaceId={unreadMarkerWorkspaceId}
              channelId={channelId}
              profileId={unreadMarkerProfileId}
              onDismiss={onUnreadMarkerDismiss}
            />
          )}
          <div
            ref={scrollerRef}
            className={cn(
              'scrollbar-hidden box-border flex w-full min-w-0 gap-2 overflow-x-auto overflow-y-hidden overscroll-x-contain pl-[var(--scroller-bleed-left)] pr-[var(--scroller-bleed-right)] [scroll-padding-left:var(--scroller-bleed-left)]',
              isPanning && 'cursor-grabbing select-none [&_*]:!cursor-grabbing',
              !isPanning && canPan && 'md:cursor-grab',
            )}
            style={isCutOffRight ? SCROLLER_EDGE_FADE_STYLE : undefined}
            {...scrollerPanningProps}
          >
            {attachments.map((attachment, attachmentIndex) => (
              <Attachment
                key={attachmentRenderKey(attachment, attachmentIndex)}
                attachment={attachment}
                mode="scroller"
                isTopRight={false}
                shouldShowUnreadMarker={false}
                channelId={channelId}
                messageId={messageId}
                messageSortOrder={messageSortOrder}
                onUnreadMarkerDismiss={onUnreadMarkerDismiss}
                unreadMarkerWorkspaceId={unreadMarkerWorkspaceId}
                unreadMarkerProfileId={unreadMarkerProfileId}
                onOpenImageLightbox={onOpenImageLightbox}
                imageGallerySlides={imageGallerySlides}
                imageGalleryIndex={imageGallery.indexByAttachmentId.get(attachment.id)}
              />
            ))}
            {trailingAccessory}
          </div>
        </div>
      </div>
    );
  }

  // Single attachment - it's always the top-right
  return (
    <div className="block">
      {/* this div is needed to force the image onto its own row above the bubble */}
      <Attachment
        key={attachmentRenderKey(attachments[0], 0)}
        attachment={attachments[0]}
        mode="single"
        isTopRight={true}
        shouldShowUnreadMarker={shouldShowUnreadMarker}
        channelId={channelId}
        messageId={messageId}
        messageSortOrder={messageSortOrder}
        onUnreadMarkerDismiss={onUnreadMarkerDismiss}
        unreadMarkerWorkspaceId={unreadMarkerWorkspaceId}
        unreadMarkerProfileId={unreadMarkerProfileId}
        onOpenImageLightbox={onOpenImageLightbox}
        imageGallerySlides={imageGallerySlides}
        imageGalleryIndex={imageGallery.indexByAttachmentId.get(attachments[0].id)}
      />
    </div>
  );
};

export default MessageAttachments;
